import React, { useMemo } from "react";
import { buildWQDetailRows } from "../../pondStat/buildWQSeries";
import { WQ_CHART_BY_ID } from "../../pondStat/wqChartConfigs";
import { buildFishRowsForBinsFromDetail } from "../../pondStat/buildFishRowsForBins";
import { binPercentsAndCumulative, globalLengthRangeForDetails } from "../../pondStat/cumulativeBinMetrics";
import PondStatTimeSeriesChart from "../../pondStat/charts/PondStatTimeSeriesChart";
import PondStatPsdVsDateChart from "../../pondStat/charts/PondStatPsdVsDateChart";
import PondStatPopulationDualAxisChart from "../../pondStat/charts/PondStatPopulationDualAxisChart";
import PondStatCumulativeLinesByDateChart from "../../pondStat/charts/PondStatCumulativeLinesByDateChart";
import { buildPsdVsDateRows } from "../../graphView/psdMetrics";
import { formatDateTime } from "../../graphView/formatters";

export default function PondDetailGraphChart({
  chartId,
  pondDetails,
  selectedFish,
  selectedUnit,
  selectedBinSize,
  isMobile,
  focusDetailId,
  precomputedPsdRows,
}) {
  const wqConfig = WQ_CHART_BY_ID[chartId];
  const binUnitLabel = selectedUnit?.value === "English" ? "inches" : "mm";

  // Only build the series this chartId needs (avoid PSD/cumulative work on WQ views, etc.).
  const wqRows = useMemo(() => {
    if (!wqConfig) return [];
    return buildWQDetailRows(pondDetails);
  }, [wqConfig, pondDetails]);

  const focusDetail = useMemo(() => {
    if (chartId !== "population-pct-cumulative-vs-bin") return null;
    return (pondDetails || []).find((d) => Number(d.id) === Number(focusDetailId)) || null;
  }, [chartId, pondDetails, focusDetailId]);

  const psdRows = useMemo(() => {
    if (chartId !== "psd-vs-date") return [];
    if (pondDetails?.length) {
      return buildPsdVsDateRows(pondDetails, selectedFish, selectedUnit?.value);
    }
    return Array.isArray(precomputedPsdRows) ? precomputedPsdRows : [];
  }, [chartId, pondDetails, selectedFish, precomputedPsdRows, selectedUnit?.value]);

  const dualAxisData = useMemo(() => {
    if (chartId !== "population-pct-cumulative-vs-bin" || !focusDetail) return null;
    const fishRows = buildFishRowsForBinsFromDetail(focusDetail, selectedFish, selectedUnit);
    const lengths = fishRows.map((r) => r.length).filter(Number.isFinite);
    if (!lengths.length) return null;
    return binPercentsAndCumulative(
      fishRows,
      { unitValue: selectedUnit.value, binSizeValue: selectedBinSize?.value },
      Math.min(...lengths),
      Math.max(...lengths)
    );
  }, [chartId, focusDetail, selectedFish, selectedUnit, selectedBinSize]);

  const cumulativeByDateSeries = useMemo(() => {
    if (chartId !== "population-cumulative-vs-bin-by-date" || !pondDetails?.length) return [];
    const { minLength, maxLength } = globalLengthRangeForDetails(pondDetails, selectedFish, selectedUnit);
    return [...pondDetails]
      .sort((a, b) => new Date(a.Date) - new Date(b.Date))
      .map((detail) => {
        const fishRows = buildFishRowsForBinsFromDetail(detail, selectedFish, selectedUnit);
        const { binLabels, cumulative } = binPercentsAndCumulative(
          fishRows,
          { unitValue: selectedUnit.value, binSizeValue: selectedBinSize?.value },
          minLength,
          maxLength
        );
        return { binLabels, cumulative, dateLabel: formatDateTime(detail.Date) };
      });
  }, [chartId, pondDetails, selectedFish, selectedUnit, selectedBinSize]);

  if (wqConfig) {
    return (
      <PondStatTimeSeriesChart
        title={wqConfig.title}
        isMobile={isMobile}
        rows={wqRows}
        seriesDefs={wqConfig.series}
        dualAxis={wqConfig.dualAxis}
        yAxisTitle={wqConfig.yAxisTitle || "mg/L"}
      />
    );
  }

  if (chartId === "psd-vs-date") {
    return <PondStatPsdVsDateChart isMobile={isMobile} rows={psdRows} />;
  }

  if (chartId === "population-pct-cumulative-vs-bin") {
    return (
      <PondStatPopulationDualAxisChart isMobile={isMobile} binUnitLabel={binUnitLabel} data={dualAxisData} />
    );
  }

  if (chartId === "population-cumulative-vs-bin-by-date") {
    return (
      <PondStatCumulativeLinesByDateChart
        isMobile={isMobile}
        binUnitLabel={binUnitLabel}
        seriesList={cumulativeByDateSeries}
      />
    );
  }

  return null;
}
